/**
 * Bridge Session Booking Dialog
 *
 * Multi-step dialog: User account (if not authenticated) → Company → Date & time.
 * On complete: POST /bridge-session-bookings, then onComplete(response) for redirect + token.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Modal,
  Steps,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  message,
  DatePicker,
} from 'antd';
import { UserOutlined, BankOutlined, CalendarOutlined } from '@ant-design/icons';
import { useUser } from '../../contexts/UserContext';
import { resolveWorkspaceCompanyId } from '../../utils/resolveWorkspaceCompanyId';
import { bridgeSessionBookingService } from '../../services/bridgeSessionBookingService';
import type { BridgeSessionBookingCreateRequest, BridgeSessionBookingCreateResponse } from '../../services/bridgeSessionBookingService';
import { getApiErrorMessage } from '../../utils/apiErrors';
import dayjs from 'dayjs';
import './BridgeSessionBookingDialog.css';

const { Step } = Steps;
const { Option } = Select;

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
}

interface CompanyFormData {
  company_name: string;
  industry: string;
  business_type: string;
  customer_count: number;
  years_in_business: number;
}

export interface BridgeSessionBookingDialogProps {
  visible: boolean;
  onClose: () => void;
  onComplete: (response: BridgeSessionBookingCreateResponse) => void;
}

export const BridgeSessionBookingDialog: React.FC<BridgeSessionBookingDialogProps> = ({
  visible,
  onClose,
  onComplete,
}) => {
  const { isAuthenticated, user, selectedCompany, availableCompanies = [], loadUserCompanies } = useUser();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userForm] = Form.useForm<UserFormData>();
  const [companyForm] = Form.useForm<CompanyFormData>();
  const [datetimeForm] = Form.useForm<{ scheduled_at: dayjs.Dayjs; company_id?: string }>();

  const resolvedWorkspaceCompanyId = useMemo(
    () => resolveWorkspaceCompanyId(user?.companyId, selectedCompany?.id, availableCompanies),
    [user?.companyId, selectedCompany?.id, availableCompanies]
  );

  const showCompanyPicker = isAuthenticated && availableCompanies.length > 1;
  const [userFormData, setUserFormData] = useState<UserFormData | null>(null);
  const [companyFormData, setCompanyFormData] = useState<CompanyFormData | null>(null);
  const companyFormDataRef = useRef<CompanyFormData | null>(null);

  const stepIndex = isAuthenticated ? currentStep + 2 : currentStep;

  useEffect(() => {
    if (!visible) {
      return;
    }
    setCurrentStep(0);
    // After paint, only the form for step 0 (guest) or datetime (auth) is mounted — reset that one.
    // Avoid resetting other useForm() instances here; they are not connected until their step renders.
    const id = requestAnimationFrame(() => {
      if (isAuthenticated) {
        datetimeForm.resetFields();
      } else {
        userForm.resetFields();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [visible, isAuthenticated, datetimeForm, userForm]);

  useEffect(() => {
    if (visible && isAuthenticated && user?.id) {
      void loadUserCompanies();
    }
  }, [visible, isAuthenticated, user?.id, loadUserCompanies]);

  useEffect(() => {
    if (visible && isAuthenticated && showCompanyPicker && resolvedWorkspaceCompanyId) {
      const cur = datetimeForm.getFieldValue('company_id');
      if (!cur) {
        datetimeForm.setFieldsValue({ company_id: resolvedWorkspaceCompanyId });
      }
    }
  }, [visible, isAuthenticated, showCompanyPicker, resolvedWorkspaceCompanyId, datetimeForm]);

  useEffect(() => {
    if (currentStep === 1 && companyFormData) {
      companyForm.setFieldsValue(companyFormData);
    }
  }, [currentStep, companyFormData, companyForm]);

  const handleUserFormFinish = (values: UserFormData) => {
    if (values.password !== values.confirmPassword) {
      message.error('Passwords do not match');
      return;
    }
    setUserFormData(values);
    setCurrentStep(1);
  };

  const handleCompanyFormFinish = (values: CompanyFormData) => {
    companyFormDataRef.current = values;
    setCompanyFormData(values);
    setCurrentStep(2);
  };

  const handleDatetimeFormFinish = async () => {
    setLoading(true);
    try {
      const values = await datetimeForm.validateFields();
      const scheduled_at = values.scheduled_at
        ? values.scheduled_at.toISOString()
        : '';

      const payload: BridgeSessionBookingCreateRequest = {
        scheduled_at,
      };

      if (isAuthenticated) {
        if (showCompanyPicker) {
          const cid = values.company_id?.trim();
          if (!cid) {
            message.error('Please select which company this session is for.');
            setLoading(false);
            return;
          }
          payload.company_id = cid;
        } else {
          const cid =
            resolvedWorkspaceCompanyId || availableCompanies[0]?.company.id || user?.companyId;
          if (!cid) {
            message.error(
              'No workspace company found. Open Control Bridge and ensure you belong to a company, then try again.'
            );
            setLoading(false);
            return;
          }
          payload.company_id = cid;
        }
      }

      if (!isAuthenticated && userFormData) {
        const company = companyFormDataRef.current ?? companyFormData;
        if (!company?.company_name || company.company_name.trim() === '') {
          message.error('Company information is missing. Please go back and complete the company step.');
          setCurrentStep(1);
          setLoading(false);
          return;
        }
        payload.user = {
          first_name: userFormData.first_name,
          last_name: userFormData.last_name,
          email: userFormData.email,
          username: userFormData.username,
          password: userFormData.password,
        };
        payload.company = {
          company_name: company.company_name,
          industry: company.industry ?? '',
          business_type: company.business_type ?? '',
          customer_count:
            typeof company.customer_count === 'number'
              ? company.customer_count
              : parseInt(String(company.customer_count ?? 0), 10),
          years_in_business:
            typeof company.years_in_business === 'number'
              ? company.years_in_business
              : parseInt(String(company.years_in_business ?? 0), 10),
        };
      }

      const response = await bridgeSessionBookingService.create(payload);
      message.success('Bridge Session booked successfully');
      onComplete(response);
      handleCancel();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      const msg =
        !axiosErr?.response
          ? 'Could not reach server. Ensure the backend is running (e.g. docker-compose up api db) and CORS allows this origin.'
          : getApiErrorMessage(err, 'Failed to book Bridge Session');
      if (process.env.NODE_ENV === 'development') {
        console.error('[BridgeSessionBooking]', err);
      }
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Only reset forms that are currently mounted; otherwise antd warns:
    // "Instance created by useForm is not connected to any Form element"
    if (isAuthenticated) {
      datetimeForm.resetFields();
    } else if (currentStep === 0) {
      userForm.resetFields();
    } else if (currentStep === 1) {
      companyForm.resetFields();
    } else {
      datetimeForm.resetFields();
    }
    setUserFormData(null);
    setCompanyFormData(null);
    companyFormDataRef.current = null;
    setCurrentStep(0);
    onClose();
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  const minDate = dayjs().add(1, 'day').startOf('day');
  const maxDate = dayjs().add(90, 'day');

  return (
    <Modal
      title="Book a Bridge Session"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      className="bridge-session-booking-dialog"
    >
      <Steps current={stepIndex} className="bridge-booking-steps">
        {!isAuthenticated && <Step title="User Account" icon={<UserOutlined />} />}
        {!isAuthenticated && <Step title="Company" icon={<BankOutlined />} />}
        <Step title="Date & Time" icon={<CalendarOutlined />} />
      </Steps>

      <div className="bridge-booking-form-container">
        {!isAuthenticated && currentStep === 0 && (
          <Form
            form={userForm}
            layout="vertical"
            onFinish={handleUserFormFinish}
            className="bridge-booking-form"
          >
            <Form.Item
              label="First Name"
              name="first_name"
              rules={[{ required: true, message: 'Please enter your first name' }]}
            >
              <Input placeholder="First name" />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="last_name"
              rules={[{ required: true, message: 'Please enter your last name' }]}
            >
              <Input placeholder="Last name" />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input placeholder="Email" />
            </Form.Item>
            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true, message: 'Please enter a username' }]}
            >
              <Input placeholder="Username" autoComplete="username" />
            </Form.Item>
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: 'Please enter a password' },
                { min: 8, message: 'Password must be at least 8 characters' },
              ]}
            >
              <Input.Password placeholder="Password" autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm password" autoComplete="new-password" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Continue to Company
              </Button>
            </Form.Item>
          </Form>
        )}

        {!isAuthenticated && currentStep === 1 && (
          <Form
            form={companyForm}
            layout="vertical"
            onFinish={handleCompanyFormFinish}
            className="bridge-booking-form"
            initialValues={{
              customer_count: 10,
              years_in_business: 1,
              industry: 'Software',
              business_type: 'B2B Services',
            }}
          >
            <Form.Item
              label="Company Name"
              name="company_name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input placeholder="Company name" />
            </Form.Item>
            <Form.Item
              label="Industry"
              name="industry"
              rules={[{ required: true, message: 'Please select industry' }]}
            >
              <Select placeholder="Select industry">
                <Option value="HR Technology">HR Technology</Option>
                <Option value="Technology Services">Technology Services</Option>
                <Option value="Software">Software</Option>
                <Option value="Consulting">Consulting</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Business Type"
              name="business_type"
              rules={[{ required: true, message: 'Please select business type' }]}
            >
              <Select placeholder="Select business type">
                <Option value="B2B Services">B2B Services</Option>
                <Option value="B2C Services">B2C Services</Option>
                <Option value="Mixed">Mixed</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Number of Customers"
              name="customer_count"
              rules={[{ required: true }, { type: 'number', min: 0 }]}
            >
              <InputNumber min={0} max={10000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="Years in Business"
              name="years_in_business"
              rules={[{ required: true }, { type: 'number', min: 0 }]}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item>
              <div className="bridge-booking-form-actions">
                <Button onClick={handleBack} style={{ marginRight: 8 }}>
                  Back
                </Button>
                <Button type="primary" htmlType="submit">
                  Continue to Date & Time
                </Button>
              </div>
            </Form.Item>
          </Form>
        )}

        {(isAuthenticated || currentStep === 2) && (
          <Form
            form={datetimeForm}
            layout="vertical"
            onFinish={handleDatetimeFormFinish}
            className="bridge-booking-form"
            initialValues={{
              scheduled_at: minDate.add(1, 'week').hour(10).minute(0).second(0),
              company_id: resolvedWorkspaceCompanyId,
            }}
          >
            {showCompanyPicker && (
              <Form.Item
                label="Company"
                name="company_id"
                rules={[{ required: true, message: 'Please select a company' }]}
              >
                <Select
                  placeholder="Select company"
                  options={availableCompanies.map((uc) => ({
                    value: uc.company.id,
                    label: uc.company.name || uc.company.id,
                  }))}
                />
              </Form.Item>
            )}
            <Form.Item
              label="Session Date & Time"
              name="scheduled_at"
              rules={[{ required: true, message: 'Please select date and time' }]}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
                minDate={minDate}
                maxDate={maxDate}
                disabledDate={(d: dayjs.Dayjs | null) => d != null && (d.isBefore(minDate) || d.isAfter(maxDate))}
              />
            </Form.Item>
            <Form.Item>
              <div className="bridge-booking-form-actions">
                {!isAuthenticated && (
                  <Button onClick={handleBack} style={{ marginRight: 8 }}>
                    Back
                  </Button>
                )}
                <Button type="primary" htmlType="submit" loading={loading}>
                  Book Bridge Session
                </Button>
              </div>
            </Form.Item>
          </Form>
        )}
      </div>
    </Modal>
  );
};
